import Link from "next/link"

interface CategoryCardProps {
  /** Category name displayed on the card */
  label: string
  /** Destination URL */
  href: string
  /** Optional image URL (shown with gradient overlay above the label) */
  imageUrl?: string
  /** Optional emoji or icon string (rendered inside a circle when no image) */
  icon?: string
  /** Optional helper text below the label (e.g. product count) */
  meta?: string
  /** Additional classes on the wrapper */
  className?: string
}

/**
 * CategoryCard — Compact clickable category tile for exploration grids.
 *
 * Renders as a small square tile with either a hero image + gradient overlay
 * or an emoji icon on a light background. Designed for dense 4-col / 8-col
 * exploration grids like the "Keep Exploring" section on collection pages.
 */
export function CategoryCard({
  label,
  href,
  imageUrl,
  icon,
  meta,
  className = "",
}: CategoryCardProps) {
  return (
    <Link
      href={href}
      className={`group relative block aspect-square rounded-xl overflow-hidden border border-gray-100 bg-white hover:border-[#ff9951]/30 hover:shadow-[0_4px_20px_rgba(0,25,69,0.08)] transition-all ${className}`}
    >
      {imageUrl ? (
        <>
          {/* Hero image */}
          <div
            className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
          {/* Dark gradient for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/35" />
          {/* Content — centered, uppercase, bold. FRIDGE-1507 K1: the title
              is sized from the tile, not the screen. "REFRIGERATORS" is one
              word about 10 of its font sizes wide, and a five-up row at 768 px
              leaves a 104 px line, so the old 18 px title ran 23 px past both
              sides of the tile (10 px at 320). 9.5% of the line keeps the
              longest word inside it, between 11 px and the old 18 px. */}
          <div className="@container relative h-full flex flex-col items-center justify-center p-3 text-center">
            <h3 className="text-white text-[length:clamp(0.6875rem,9.5cqi,1.125rem)] font-black uppercase tracking-widest leading-snug group-hover:text-[#ff9951] transition-colors">
              {label}
            </h3>
            {meta && <p className="text-white/70 text-[10px] uppercase tracking-widest mt-1">{meta}</p>}
          </div>
        </>
      ) : (
        <div className="h-full flex flex-col items-center justify-center p-4 text-center bg-gradient-to-br from-gray-50 to-white">
          {icon && (
            <div className="w-14 h-14 rounded-xl bg-[#0469f0]/10 flex items-center justify-center mb-3 text-3xl group-hover:bg-[#0469f0]/20 transition-colors">
              {icon}
            </div>
          )}
          <h3 className="text-base font-bold text-gray-900 leading-tight line-clamp-2 group-hover:text-[#0469f0] transition-colors">
            {label}
          </h3>
          {meta && <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">{meta}</p>}
        </div>
      )}
    </Link>
  )
}
